import { useEffect } from 'react';

type Sentinel = { release: () => Promise<void>; addEventListener: (type: 'release', cb: () => void) => void };
type WakeLockNavigator = Navigator & { wakeLock?: { request: (type: 'screen') => Promise<Sentinel> } };

/**
 * Mantém a tela do aparelho ligada enquanto `active` for verdadeiro (Screen Wake Lock API).
 * Sem isso, o celular apaga a tela no meio de uma conversa por voz e o navegador corta o microfone.
 * O sistema solta a trava sozinho quando a aba some (troca de app, bloqueio manual): ao voltar, pedimos de novo.
 * Navegador sem suporte (iOS antes do 16.4, alguns Android antigos) simplesmente não trava: nada quebra.
 */
export function useWakeLock(active: boolean) {
  useEffect(() => {
    const nav = navigator as WakeLockNavigator;
    if (!active || !nav.wakeLock) return;
    let lock: Sentinel | null = null;
    let cancelled = false;
    const acquire = async () => {
      if (cancelled || document.visibilityState !== 'visible' || lock) return;
      try {
        const got = await nav.wakeLock!.request('screen');
        if (cancelled) { void got.release().catch(() => undefined); return; }
        lock = got;
        got.addEventListener('release', () => { if (lock === got) lock = null; });
      } catch { /* bateria fraca ou economia de energia: o sistema recusou, segue sem trava */ }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') void acquire(); };
    void acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release().catch(() => undefined);
      lock = null;
    };
  }, [active]);
}
